/**
 * «منشی هوشمند پارسینا» — one result's session, behind its window (PM-0080).
 * Logic only: state, the microphone, the BFF calls. The screen is SecretaryWindow.jsx.
 *
 * The COMMAND was recorded and run by the record button (useSecretaryController);
 * this session opens on what it brought back — `initial`: the text, the result and
 * whatever was already chosen or answered — and finishes it:
 *
 *   result ── choose a patient (or «بیمار جدید»), a doctor, or correct the text ──► processing ──► result
 *     │
 *     └── «ادامه بگویید» ──► listening ──► the recording is APPENDED to what was heard,
 *                                          and the whole sentence runs again
 *
 * Every change goes back to the item through `onChange`, so closing the window
 * loses nothing: the notification comes back as it was left. «دوباره بگویید» is
 * a NEW command, so it belongs to the record button (`onRecordAgain`).
 *
 * ── SPEECH IS SERVER-SIDE, AND STARTING IS THE USER'S PRESS ──────────────────
 * The audio goes to the BFF's /ws/transcribe stream (`useServerVoiceInput`), never
 * to the browser's Web Speech API (PM-0013). Nothing here starts on a timer: a
 * recording starts on «ادامه بگویید», or — below — when a question the desk opened
 * asks for an answer.
 *
 * ── WHAT THE SENTENCE LEFT OUT ───────────────────────────────────────────────
 * A result may carry `questions` (model.openQuestion), answered ON the sheet: a
 * tapped quick answer, or a mobile the desk confirmed after it was read back, goes
 * to refineSecretaryCommand — rebuilt in code, no model, no پارسه. A blocking
 * question opens the microphone by itself (the user's press opened the secretary,
 * and the sheet asked), and that recording ends by itself on silence
 * (silenceDetector.js); one that never hears speech is thrown away unsent. Only a
 * FREE spoken answer («پنجشنبه عصر») is added to the sentence and runs the command
 * again. What a tap or a read-back settled travels with every later run as `facts`
 * — words the planner reads — so running the sentence again never forgets a mobile
 * the desk already confirmed.
 *
 * ── A COMMAND IS ASKED ONCE ──────────────────────────────────────────────────
 * `runId` makes a superseded answer inert: «دوباره بگویید» while a slow answer is
 * still in flight must not have that answer land on top of the new recording.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { bffMutation, bffQuery } from '../lib/bffClient';
import { useCredits } from '../hooks/useCredits';
import { useServerVoiceInput } from '../hooks/useServerVoiceInput';
import { useTranslation } from '../i18n';
import {
  PHASE, PRICE_TRANSCRIPTION, REFINE_SECRETARY_COMMAND, RUN_SECRETARY_COMMAND, SKIPPABLE_QUESTIONS,
  answerFor, digitsFromSpeech, draftInput, isCreditRefusal, matchSpokenOption, mergeRefined,
  openBlockingCount, openQuestion,
} from './model';

const MIC_FAILURES = new Set(['denied', 'unsupported', 'error']);
// A microphone that refused once is not opened again by itself.
const MIC_REFUSED = new Set(['denied', 'unsupported']);
// Words that do not tell one quick answer from another.
// i18n-exempt: words MATCHED in a spoken answer, never rendered.
const IGNORED_WORDS = {
  doctor: ['دکتر', 'خانم', 'آقای', 'آقا', 'پیش', 'با', 'doctor', 'dr', 'الدكتور', 'دكتور'],
  insurance: ['بیمه', 'insurance'],
};
// A choice must BE the chip; anything more goes back to the planner.
const CHOICE_KINDS = new Set(['when', 'followUpDate']);

/** Where a window opens: on the item's error, else on its result. */
function phaseOf(initial) {
  if (initial?.error === 'credit') return PHASE.NO_CREDIT;
  if (initial?.error === 'failed' || !initial?.result) return PHASE.ERROR;
  return PHASE.RESULT;
}

export function useSecretary({ initial = null, onChange = null, onRecordAgain = null } = {}) {
  const { t } = useTranslation();
  // The ends of an ANSWER's recording, read through refs: they are defined further down.
  const onSilenceRef = useRef(null);
  const onNoSpeechRef = useRef(null);
  const onNoAutoStopRef = useRef(null);
  const voice = useServerVoiceInput({
    onSilence: () => onSilenceRef.current?.(),
    onNoSpeech: () => onNoSpeechRef.current?.(),
    onAutoStopUnavailable: () => onNoAutoStopRef.current?.(),
  });
  const credits = useCredits();

  const [phase, setPhase] = useState(() => phaseOf(initial));
  const [text, setText] = useState(initial?.text || '');
  const [result, setResult] = useState(initial?.result || null);
  const [selections, setSelections] = useState(initial?.selections || {});
  const [seconds, setSeconds] = useState(0);
  // Whether «پارسه کافی نیست» was found BEFORE recording (nothing ran) or by the server.
  const [creditAfterRun, setCreditAfterRun] = useState(initial?.error === 'credit' && Boolean(initial?.text));
  // Questions closed in this command: skipped (the form asks), and answered.
  const [skipped, setSkipped] = useState(() => new Set(initial?.skipped || []));
  const [answered, setAnswered] = useState(() => new Set(initial?.answered || []));
  // The answer under way: { kind, stage: listening | hearing | readback | refining | idle,
  // digits, heard, note, manual }.
  const [answer, setAnswerState] = useState(null);

  // The voice hook's callbacks are read through a ref so the effects below depend
  // on what the session is doing, not on the hook's identity from render to render.
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const creditsRef = useRef(credits);
  creditsRef.current = credits;
  const resultRef = useRef(result);
  resultRef.current = result;
  const textRef = useRef(text);
  textRef.current = text;
  const selectionsRef = useRef(selections);
  selectionsRef.current = selections;
  const skippedRef = useRef(skipped);
  skippedRef.current = skipped;
  const answerRef = useRef(answer);
  const runId = useRef(0);
  // What answers settled, as words the planner reads — added to every later run.
  const facts = useRef(new Map(initial?.facts || []));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onRecordAgainRef = useRef(onRecordAgain);
  onRecordAgainRef.current = onRecordAgain;
  // «ادامه بگویید»: the recording under way adds to `text` instead of replacing it.
  const appending = useRef(false);
  const [continuing, setContinuing] = useState(false);

  // An answer's recording ends from outside React (the silence watch): the ref is
  // what those callbacks read, so it moves with the state, never a render behind.
  const setAnswer = useCallback((next) => {
    answerRef.current = next;
    setAnswerState(next);
  }, []);

  const listen = useCallback(() => {
    setSeconds(0);
    setPhase(PHASE.LISTENING);
    voiceRef.current.start();
  }, []);

  // ── Everything the session settles goes back to its item ────────────────────
  const reported = useRef(false);
  useEffect(() => {
    // Not the values it opened with: they came FROM the item.
    if (!reported.current) { reported.current = true; return; }
    onChangeRef.current?.({
      text, result, selections,
      skipped: [...skipped], answered: [...answered], facts: [...facts.current.entries()],
      error: phase === PHASE.NO_CREDIT ? 'credit' : phase === PHASE.ERROR ? 'failed' : null,
    });
  }, [text, result, selections, skipped, answered, phase]);

  // ── The microphone refused ──────────────────────────────────────────────────
  useEffect(() => {
    if (phase === PHASE.LISTENING && MIC_FAILURES.has(voice.status)) setPhase(PHASE.NO_MIC);
    // An answer's recording failing leaves the sheet as it is: the quick answers still work.
    const a = answerRef.current;
    if (phase === PHASE.RESULT && a?.stage === 'listening' && MIC_FAILURES.has(voice.status)) {
      setAnswer({ kind: a.kind, stage: 'idle', note: 'mic' });
    }
  }, [phase, voice.status, setAnswer]);

  // ── The recording clock: what is on screen while the meter runs ─────────────
  const recording = phase === PHASE.LISTENING && voice.status === 'listening';
  useEffect(() => {
    if (!recording) return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [recording]);

  // Leaving the overlay releases the microphone, whatever state it was in.
  useEffect(() => () => {
    runId.current += 1;
    voiceRef.current.cancel();
  }, []);

  /** Forget every answer of the command: a NEW command starts from nothing. */
  const forgetAnswers = useCallback(() => {
    facts.current = new Map();
    setSkipped(new Set());
    setAnswered(new Set());
    setAnswer(null);
  }, [setAnswer]);

  const submit = useCallback(async (transcript, chosen = {}) => {
    const id = runId.current + 1;
    runId.current = id;
    // A question still listening is not the answer to this run: drop it, unsent.
    if (answerRef.current?.stage === 'listening') voiceRef.current.cancel();
    setSelections(chosen);
    setAnswer(null);
    setPhase(PHASE.PROCESSING);
    try {
      const data = await bffMutation(RUN_SECRETARY_COMMAND, {
        input: {
          transcript: [transcript, ...facts.current.values()].join(' ').trim(),
          patientId: chosen.patientId || null,
          doctorId: chosen.doctorId || null,
          newPatient: !!chosen.newPatient,
          // This panel answers what the sentence left out (refineSecretaryCommand).
          questions: true,
        },
      });
      if (id !== runId.current) return;
      setResult(data?.runSecretaryCommand || { outcome: 'UNKNOWN', charged: false });
      setPhase(PHASE.RESULT);
    } catch (err) {
      if (id !== runId.current) return;
      if (isCreditRefusal(err)) {
        setCreditAfterRun(true);
        setPhase(PHASE.NO_CREDIT);
      } else {
        setPhase(PHASE.ERROR);
      }
    } finally {
      // The balance on the sidebar and the next «can I afford it» both read this.
      creditsRef.current.refresh?.();
    }
  }, [setAnswer]);

  /** «توقف و اجرا» — stop recording, take the server's final transcript, run it. */
  const finish = useCallback(async () => {
    // Not before the stream is up: a stop pressed while the microphone is still
    // connecting would send nothing and answer «چیزی نشنیدم» to someone who has not
    // been heard yet.
    if (phase !== PHASE.LISTENING || voiceRef.current.status !== 'listening') return;
    const id = runId.current + 1;
    runId.current = id;
    setPhase(PHASE.PROCESSING);
    const heard = String((await voiceRef.current.finishTranscript()) || '').trim();
    if (id !== runId.current) return;
    if (appending.current) {
      appending.current = false;
      setContinuing(false);
      // Nothing added: the sheet comes back as it was, and nothing is run or charged.
      if (!heard) { setPhase(PHASE.RESULT); return; }
      const whole = `${text} ${heard}`.trim();
      setText(whole);
      // The same command, said more fully: the choices already made still stand.
      await submit(whole, selections);
      return;
    }
    setText(heard);
    if (!heard) {
      // Nothing to send: no command was run, so nothing is asked of the BFF.
      setResult({ outcome: 'UNKNOWN', reason: 'nothing_heard', charged: false });
      setPhase(PHASE.RESULT);
      return;
    }
    await submit(heard, {});
  }, [phase, submit, text, selections]);

  /** «دوباره بگویید» — drop this command; the record button records the new one. */
  const again = useCallback(() => {
    runId.current += 1;
    voiceRef.current.cancel();
    appending.current = false;
    setContinuing(false);
    onRecordAgainRef.current?.();
  }, []);

  /** «اجرا با این متن» — the user corrected what was heard. A new command, no selections. */
  const runEdited = useCallback((edited) => {
    const next = String(edited || '').trim();
    if (!next) return;
    forgetAnswers();
    setText(next);
    submit(next, {});
  }, [submit, forgetAnswers]);

  const retry = useCallback(() => submit(text, selections), [submit, text, selections]);

  /**
   * «ادامه بگویید» — record MORE of the same command: the mobile, the insurer, the
   * hour the sheet says is missing. What is heard is appended to the sentence so far.
   */
  const continueSpeaking = useCallback(() => {
    if (!text) return;
    runId.current += 1;
    voiceRef.current.cancel();
    setAnswer(null);
    appending.current = true;
    setContinuing(true);
    listen();
  }, [listen, text, setAnswer]);

  /** Back out of «ادامه بگویید» to the sheet as it was — nothing recorded is used. */
  const cancelContinue = useCallback(() => {
    runId.current += 1;
    voiceRef.current.cancel();
    appending.current = false;
    setContinuing(false);
    setPhase(result ? PHASE.RESULT : PHASE.LISTENING);
  }, [result]);

  // ── Answers ─────────────────────────────────────────────────────────────────

  /** A question's quick answers, each with the words the desk reads (and may say). */
  const optionsOf = useCallback((question) => (question?.options || []).map((o) => ({
    ...o,
    label: CHOICE_KINDS.has(question.kind) ? t(`secretary.question.choice.${question.kind}.${o.value}`) : (o.label || ''),
  })), [t]);

  /**
   * An answer the BFF can take as it is: rebuilt in code from the result's draft.
   * The recording, if one is open, is dropped unsent — the tap is the answer.
   */
  const refine = useCallback(async (kind, value, label) => {
    const current = resultRef.current;
    const answerInput = answerFor(kind, value);
    if (!current?.draft || !answerInput) return;
    const id = runId.current + 1;
    runId.current = id;
    voiceRef.current.cancel();
    setAnswer({ kind, stage: 'refining' });
    try {
      const data = await bffQuery(REFINE_SECRETARY_COMMAND, { draft: draftInput(current.draft), answer: answerInput });
      if (id !== runId.current) return;
      const next = data?.refineSecretaryCommand;
      if (!next) throw new Error('refineSecretaryCommand returned nothing');
      // Still asked: the answer did not settle it (a mobile the clinic cannot use).
      const still = (next.questions || []).some((q) => q.kind === kind && q.blocking);
      if (!still) {
        if (kind === 'doctor') setSelections((s) => ({ ...s, doctorId: String(value) }));
        else if (kind === 'when' && value === 'first') facts.current.delete('when');
        else facts.current.set(kind, t(`secretary.question.fact.${kind}`, { value: label || value }));
        if (kind !== 'when') setAnswered((s) => new Set(s).add(kind));
      }
      setAnswer(still && kind === 'phone' ? { kind, stage: 'idle', note: 'invalid_phone', heard: String(value) } : null);
      setResult(mergeRefined(current, next));
    } catch {
      if (id !== runId.current) return;
      setAnswer({ kind, stage: 'idle', note: 'failed' });
    }
  }, [t, setAnswer]);

  /** What a spoken answer was: digits to read back, a quick answer, or — for a time — more of the sentence. */
  const heardAnswer = useCallback((kind, heard) => {
    const question = openQuestion(resultRef.current, skippedRef.current);
    if (!question || question.kind !== kind) { setAnswer(null); return; }
    if (!heard) { setAnswer({ kind, stage: 'idle', note: 'not_heard' }); return; }
    if (kind === 'phone') {
      // Never used as heard: speech gets digits wrong, so the desk reads them first.
      const digits = digitsFromSpeech(heard);
      setAnswer(digits ? { kind, stage: 'readback', digits, heard } : { kind, stage: 'idle', note: 'no_digits', heard });
      return;
    }
    const choice = CHOICE_KINDS.has(kind);
    const hit = matchSpokenOption(heard, optionsOf(question), { exact: choice, ignore: IGNORED_WORDS[kind] || [] });
    if (hit) { refine(kind, hit.value, hit.label); return; }
    if (choice) {
      // A free answer («پنجشنبه عصر») is more of the sentence, as «ادامه بگویید» is: it
      // is added to what was heard, replaces a chip tapped for the same question, and
      // the command runs again.
      facts.current.delete(kind);
      const whole = `${textRef.current} ${heard}`.trim();
      setText(whole);
      submit(whole, selectionsRef.current);
      return;
    }
    setAnswer({ kind, stage: 'idle', note: 'not_matched', heard });
  }, [optionsOf, refine, submit, setAnswer]);

  /** Listen for the answer to the question on screen. Ends by itself on silence. */
  const answerByVoice = useCallback((options = {}) => {
    const question = openQuestion(resultRef.current, skippedRef.current);
    if (!question) return;
    runId.current += 1;
    voiceRef.current.cancel();
    const previous = answerRef.current;
    // «این شماره درست نیست» stays on screen while the number is asked for again.
    const note = options.keepNote && previous?.kind === question.kind ? previous.note || null : null;
    setAnswer({ kind: question.kind, stage: 'listening', note, manual: false });
    voiceRef.current.start({ autoStop: true });
  }, [setAnswer]);

  /** The answer's recording is over: take what the server heard. */
  const finishAnswer = useCallback(async () => {
    const a = answerRef.current;
    // Not before the stream is up — the same rule as «توقف و اجرا».
    if (!a || a.stage !== 'listening' || voiceRef.current.status !== 'listening') return;
    const id = runId.current + 1;
    runId.current = id;
    setAnswer({ ...a, stage: 'hearing', manual: false });
    const heard = String((await voiceRef.current.finishTranscript()) || '').trim();
    if (id !== runId.current) return;
    heardAnswer(a.kind, heard);
  }, [heardAnswer, setAnswer]);

  /** Stop listening for an answer — nothing recorded is sent, the question stays. */
  const cancelAnswer = useCallback(() => {
    const a = answerRef.current;
    if (!a || (a.stage !== 'listening' && a.stage !== 'hearing')) return;
    runId.current += 1;
    voiceRef.current.cancel();
    setAnswer(null);
  }, [setAnswer]);

  onSilenceRef.current = finishAnswer;
  onNoSpeechRef.current = () => {
    const a = answerRef.current;
    if (!a || a.stage !== 'listening') return;
    // Nobody answered: the recording is thrown away unsent — and so costs nothing.
    voiceRef.current.cancel();
    setAnswer({ kind: a.kind, stage: 'idle', note: 'not_heard' });
  };
  onNoAutoStopRef.current = () => {
    const a = answerRef.current;
    // This browser will not let the level be read: the desk ends the answer itself.
    if (a?.stage === 'listening') setAnswer({ ...a, manual: true });
  };

  /** A tapped quick answer (a chip, or keys 1–5). */
  const answerWith = useCallback((value) => {
    const question = openQuestion(resultRef.current, skippedRef.current);
    if (!question) return;
    const option = optionsOf(question).find((o) => o.value === String(value));
    if (!option) return;
    refine(question.kind, option.value, option.label);
  }, [optionsOf, refine]);

  /** «بله، درست است» — the digits read back are the mobile. */
  const confirmPhone = useCallback(() => {
    const a = answerRef.current;
    if (a?.kind !== 'phone' || a.stage !== 'readback' || !a.digits) return;
    refine('phone', a.digits, a.digits);
  }, [refine]);

  /** «رد کردن، در فرم وارد می‌کنم» — the form asks for it instead. */
  const skipQuestion = useCallback(() => {
    const question = openQuestion(resultRef.current, skippedRef.current);
    if (!question || !SKIPPABLE_QUESTIONS.includes(question.kind)) return;
    runId.current += 1;
    voiceRef.current.cancel();
    setAnswer(null);
    setSkipped((s) => new Set(s).add(question.kind));
  }, [setAnswer]);

  const question = phase === PHASE.RESULT ? openQuestion(result, skipped) : null;

  // ── A blocking question opens the microphone by itself — once per question ──
  const autoAsked = useRef({ result: null, kinds: new Set() });
  useEffect(() => {
    if (!question?.blocking) return;
    if (autoAsked.current.result !== result) autoAsked.current = { result, kinds: new Set() };
    if (autoAsked.current.kinds.has(question.kind)) return;
    autoAsked.current.kinds.add(question.kind);
    if (MIC_REFUSED.has(voiceRef.current.status)) return;
    // Known not to cover even a minute of transcription: the quick answers still work.
    const { balance, priceOf } = creditsRef.current;
    const minute = priceOf?.(PRICE_TRANSCRIPTION);
    if (Number.isFinite(balance) && Number.isFinite(minute) && balance < minute) return;
    const a = answerRef.current;
    if (a && a.kind === question.kind && (a.stage === 'refining' || a.stage === 'readback')) return;
    answerByVoice({ keepNote: true });
  }, [question?.kind, question?.blocking, result, answerByVoice]);

  // Doctor chosen on CHOOSE_DOCTOR: free when the result can be refined, a re-run when not.
  const chooseDoctor = useCallback((doctorId) => {
    const current = resultRef.current;
    if (current?.draft && (current.questions || []).some((q) => q.kind === 'doctor')) {
      const doctor = (current.doctors || []).find((d) => d.doctorId === doctorId);
      refine('doctor', doctorId, doctor?.name || '');
      return;
    }
    submit(text, { ...selections, doctorId });
  }, [refine, submit, text, selections]);

  const choosePatient = useCallback((patientId) => {
    // A mobile and an insurer answered for a NEW patient describe nobody on file.
    facts.current.delete('phone');
    facts.current.delete('insurance');
    return submit(text, { ...selections, patientId, newPatient: false });
  }, [submit, text, selections]);
  /** «هیچ‌کدام؛ بیمار جدید است» — none of the candidates: book for a patient with no file yet. */
  const chooseNewPatient = useCallback(
    () => submit(text, { ...selections, patientId: null, newPatient: true }),
    [submit, text, selections],
  );

  // «۱ از ۲»: the blocking questions settled so far, and those still open.
  const openKinds = new Set((result?.questions || []).map((q) => q.kind));
  const settled = [...new Set([...skipped, ...answered])].filter((k) => !openKinds.has(k) || skipped.has(k)).length;
  const step = question?.blocking
    ? { index: settled + 1, total: settled + openBlockingCount(result, skipped) }
    : null;

  return {
    phase, text, result, seconds, recording, creditAfterRun, continuing,
    micStatus: voice.status,
    interim: voice.transcript || '',
    priceOf: credits.priceOf,
    question: question ? { ...question, options: optionsOf(question), step } : null,
    answer: answer && question && answer.kind === question.kind ? answer : null,
    skipped,
    selections,
    finish, again, continueSpeaking, cancelContinue, choosePatient, chooseNewPatient, chooseDoctor, runEdited, retry,
    answerWith, answerByVoice, finishAnswer, cancelAnswer, confirmPhone, skipQuestion,
  };
}

export default useSecretary;
